import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, LogOut, TrendingUp, Users, DollarSign, Package, 
  AlertCircle, CheckCircle, Activity, BarChart3, PieChart,
  Calendar, ArrowUp, ArrowDown, Home
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

const BusinessDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [overview, setOverview] = useState(null);
  const [revenueChart, setRevenueChart] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [transactionAnalytics, setTransactionAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  useEffect(() => {
    fetchAllData();
  }, [selectedPeriod]);

  const fetchAllData = async () => {
    try {
      const [overviewRes, revenueRes, userRes, transactionRes] = await Promise.all([
        axios.get(`${API}/business/overview`),
        axios.get(`${API}/business/revenue-chart?days=${selectedPeriod}`),
        axios.get(`${API}/business/user-analytics`),
        axios.get(`${API}/business/transaction-analytics`)
      ]);

      setOverview(overviewRes.data);
      setRevenueChart(revenueRes.data);
      setUserAnalytics(userRes.data);
      setTransactionAnalytics(transactionRes.data);
    } catch (error) {
      toast.error("Failed to load business data");
    }
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 to-pink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={() => navigate('/')}
            >
              <img src="/logo-dark-clean.png" alt="Middleman" className="h-20 w-auto" />
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <Home className="h-4 w-4" />
                <span>Retour au site</span>
              </Button>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <Badge variant="secondary" className="text-xs">{t('roles.owner')}</Badge>
              </div>
              <LanguageSwitcher />
              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="business-logout-button"
                className="flex items-center space-x-2"
              >
                <LogOut className="h-4 w-4" />
                <span>{t('common.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Key Metrics */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Revenus totaux</CardTitle>
              <DollarSign className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {formatCurrency(overview?.total_revenue || 0)}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {formatCurrency(overview?.month_revenue || 0)} ce mois
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-brand-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Frais plateforme</CardTitle>
              <TrendingUp className="h-5 w-5 text-brand-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {formatCurrency(overview?.platform_fees_collected || 0)}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                5% des transactions complétées
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-brand-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Utilisateurs totaux</CardTitle>
              <Users className="h-5 w-5 text-brand-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {formatNumber(overview?.total_users || 0)}
              </div>
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <ArrowUp className="h-3 w-3 mr-1" />
                +{overview?.new_users_month || 0} ce mois
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Transferts</CardTitle>
              <Package className="h-5 w-5 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {formatNumber(overview?.total_transactions || 0)}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {overview?.active_transactions || 0} actifs
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Metrics */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                Taux de succès
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {100 - (overview?.dispute_rate || 0)}%
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {overview?.completed_transactions || 0} transferts complétés
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 text-yellow-600" />
                Taux de litiges
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {overview?.dispute_rate || 0}%
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {overview?.disputed_transactions || 0} transferts contestés
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center">
                <Activity className="h-4 w-4 mr-2 text-brand-600" />
                Transfert moyen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-brand-600">
                {formatCurrency(overview?.average_transaction_value || 0)}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Valeur par transfert
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Revenue Chart */}
          <Card className="md:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-brand-600" />
                    Aperçu des revenus
                  </CardTitle>
                  <CardDescription>Revenus quotidiens des {selectedPeriod} derniers jours</CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant={selectedPeriod === 7 ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedPeriod(7)}
                  >
                    7 jours
                  </Button>
                  <Button
                    variant={selectedPeriod === 30 ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedPeriod(30)}
                  >
                    30 jours
                  </Button>
                  <Button
                    variant={selectedPeriod === 90 ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedPeriod(90)}
                  >
                    90 jours
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {revenueChart?.data?.slice(-10).map((day, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-24 text-sm text-gray-600">
                      {new Date(day.date).toLocaleDateString('fr-CA', { month: 'short', day: 'numeric' })}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="h-8 bg-gradient-to-r from-brand-500 to-pink-500 rounded"
                          style={{ width: `${(day.revenue / Math.max(...(revenueChart?.data || []).map(d => d.revenue))) * 100}%` }}
                        />
                        <span className="text-sm font-semibold text-gray-900">
                          {formatCurrency(day.revenue)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {day.transactions} transferts • {formatCurrency(day.fees)} frais
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* User Analytics */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-brand-600" />
                Analyse utilisateurs
              </CardTitle>
              <CardDescription>Engagement et distribution</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Utilisateurs actifs</span>
                    <span className="text-sm font-semibold">{userAnalytics?.active_users || 0}</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500"
                      style={{ width: `${((userAnalytics?.active_users || 0) / (userAnalytics?.total_users || 1)) * 100}%` }}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Distribution des rôles</h4>
                  <div className="space-y-2">
                    {Object.entries(userAnalytics?.role_distribution || {}).map(([role, count]) => (
                      <div key={role} className="flex justify-between items-center">
                        <span className="text-sm capitalize text-gray-600">{role}</span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Croissance utilisateurs (7 derniers jours)</h4>
                  <div className="flex items-end space-x-1 h-20">
                    {userAnalytics?.user_growth?.map((day, index) => (
                      <div key={index} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-brand-500 rounded-t"
                          style={{ 
                            height: `${(day.new_users / Math.max(...(userAnalytics?.user_growth || []).map(d => d.new_users || 1))) * 100}%`,
                            minHeight: day.new_users > 0 ? '4px' : '0'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>Il y a 7j</span>
                    <span>Aujourd'hui</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transaction Analytics */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <PieChart className="h-5 w-5 mr-2 text-orange-600" />
                Analyse des transferts
              </CardTitle>
              <CardDescription>Performance et répartition par statut</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Taux de succès</span>
                    <span className="text-sm font-semibold text-green-600">
                      {transactionAnalytics?.success_rate || 0}%
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500"
                      style={{ width: `${transactionAnalytics?.success_rate || 0}%` }}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Répartition par statut</h4>
                  <div className="space-y-2">
                    {Object.entries(transactionAnalytics?.status_breakdown || {}).map(([status, count]) => (
                      <div key={status} className="flex justify-between items-center">
                        <span className="text-sm capitalize text-gray-600">
                          {status.replace(/_/g, ' ')}
                        </span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-xs text-gray-600">Succès OCR</p>
                    <p className="text-lg font-bold text-brand-600">
                      {transactionAnalytics?.ocr_success_rate || 0}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Traitement moyen</p>
                    <p className="text-lg font-bold text-brand-600">
                      {transactionAnalytics?.average_processing_hours || 0}h
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions & Reports</CardTitle>
            <CardDescription>Export data and manage platform settings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <Button variant="outline" className="justify-start">
                <Calendar className="h-4 w-4 mr-2" />
                Export Monthly Report
              </Button>
              <Button variant="outline" className="justify-start">
                <Users className="h-4 w-4 mr-2" />
                Export User Data
              </Button>
              <Button variant="outline" className="justify-start">
                <Package className="h-4 w-4 mr-2" />
                Export Transactions
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BusinessDashboard;
